import { FC, ReactNode, useState } from 'react';

import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';

import { Text } from '../../Text';

import styles from './field.module.scss';

interface IFieldProps {
  label?: string;
  labelContent?: ReactNode;
  error?: boolean;
  helpText?: ReactNode;
  errorMessage?: ReactNode;
  wrapperClassName?: string;
}

let nextFieldId = 0;

const useField = (
  id: string | undefined,
  describedBy: string | undefined,
  helpText: ReactNode,
  errorMessage: ReactNode,
  error?: boolean,
): { fieldId: string; descriptionIds: string | undefined } => {
  const [generatedId] = useState(() => {
    nextFieldId += 1;
    return `form-field-${nextFieldId}`;
  });
  const fieldId = id ?? generatedId;
  const ids = [
    describedBy,
    helpText ? `${fieldId}-help` : undefined,
    error && errorMessage ? `${fieldId}-error` : undefined,
  ]
    .filter(Boolean)
    .join(' ');
  return { fieldId, descriptionIds: ids || undefined };
};

/** Shared label association and supporting descriptions for form controls. */
const Field: FC<IFieldProps & { fieldId: string; hug?: boolean }> = ({
  fieldId,
  label,
  labelContent,
  error,
  helpText,
  errorMessage,
  wrapperClassName,
  hug,
  children,
}) => (
  <div className={classNames(styles.field, { [styles.hug]: hug }, wrapperClassName)}>
    {(labelContent || label) && (
      <label
        id={`${fieldId}-label`}
        htmlFor={fieldId}
        className={classNames(styles.label, { [styles['label--error']]: error })}
      >
        {labelContent || <FormattedMessage id={label!} />}
      </label>
    )}
    {children}
    {helpText && (
      <div id={`${fieldId}-help`} className={styles.message}>
        <Text variant="bodySmall" color="var(--sk-text-subtle)">
          {helpText}
        </Text>
      </div>
    )}
    {error && errorMessage && (
      <div id={`${fieldId}-error`} className={styles.message}>
        <Text variant="bodySmall" color="var(--sk-text-danger)">
          {errorMessage}
        </Text>
      </div>
    )}
  </div>
);

export { Field, useField };
export type { IFieldProps };
